import React, { useEffect, useState, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useToast } from "../../../hooks/toast";
import api from "../../../service";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import ControlledInput from "../../../components/ControlledInputs/input";
import ControlledSelect from "../../../components/ControlledInputs/select";
import ControlledTextareaInput from "../../../components/ControlledInputs/input-textarea";
import ControlledDatePicker from "../../../components/ControlledInputs/date-picker-antd";
import ControlledSwitch from "../../../components/ControlledInputs/switch";
import FormButton from "../../../components/FormButton";
import { useLoading } from "../../../hooks/loading";
import productTypesConstants from "../../../constants/productTypes";
import {
  Container,
  TitleContainer,
  Title,
  Session,
  FieldsContainer,
  ActionContainer,
  SubmitContainer,
  Horizontal,
  AddScheduleButton,
  RemoveScheduleButton,
  ScheduleItem,
  IndexSchedule,
} from "./styles";
import { parse } from "date-fns";

import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { RiAddCircleLine } from "react-icons/ri";
import { FaRegTrashAlt } from "react-icons/fa";

const ProductEventAssociationItem = () => {
  const intl = useIntl();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);
  const language = useSelector((state) => state.language).code;
  const history = useHistory();
  const { handleLoading } = useLoading();
  const { id } = useParams();

  const [loadEditData, setLoadEditData] = useState();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState();
  const [salesforceProducts, setSalesforceProducts] = useState();
  const [products, setProducts] = useState([]);
  const [unities, setUnities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const productTypes = productTypesConstants();

  const getDefaultFormValues = () => ({
    salesforceEquivalentProduct: null,
    active: true,
    showWarningMessage: false,
    fractionatedQuantity: false,
    fixedFinalDateAssembly: false,
    warningMessage: "",
    scheduledValuesList: [],
  });

  const decimalValueRegex = /^[0-9]+(\.[0-9]+)?$/g;

  const scheduledValuesListSchema = z.object({
    id: z.coerce.number().nullish(),
    scheduledDate: z.date({
      required_error: intl.formatMessage({ id: "error.field_required" }),
    }),
    realScheduledValue: z
      .string()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .regex(decimalValueRegex, "error.need_to_be_decimal"),
    dollarScheduledValue: z
      .string()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .regex(decimalValueRegex, "error.need_to_be_decimal"),
    euroScheduledValue: z
      .string()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .regex(decimalValueRegex, "error.need_to_be_decimal"),
  });

  const schema = z.object({
    event: z.object({
      value: z
        .string()
        .min(1, intl.formatMessage({ id: "error.field_required" })),
      label: z.string(),
    }),
    product: z.object({
      value: z.coerce
        .number()
        .min(1, intl.formatMessage({ id: "error.field_required" })),
      label: z.string(),
    }),
    productType: z.object({
      value: z.coerce
        .number()
        .min(0, intl.formatMessage({ id: "error.field_required" })),
      label: z.string(),
    }),
    unity: z.object({
      value: z.coerce
        .number()
        .min(1, intl.formatMessage({ id: "error.field_required" })),
      label: z.string(),
    }),
    realValue: z
      .string()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .regex(decimalValueRegex, "error.need_to_be_decimal"),
    dollarValue: z
      .string()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .regex(decimalValueRegex, "error.need_to_be_decimal"),
    euroValue: z
      .string()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .regex(decimalValueRegex, "error.need_to_be_decimal"),
    scheduledValuesList: z
      .array(scheduledValuesListSchema)
      .nullable()
      .optional(),
    deadline: z.date().nullable().optional(),
    maxDate: z.date().nullable().optional(),
    startDateAssembly: z.date().nullable().optional(),
    finalDateAssembly: z
      .date()
      .nullable()
      .optional()
      .refine(
        (finalDateAssembly) => {
          if (watchFixedFinalDateAssembly) {
            return !!finalDateAssembly;
          }

          return true;
        },
        {
          message: intl.formatMessage({
            id: "error.field_required",
          }),
        },
      ),
    salesforceEquivalentProduct: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullable()
      .optional(),
    period: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullable()
      .optional(),
    active: z.boolean(),
    showWarningMessage: z.boolean(),
    fractionatedQuantity: z.boolean(),
    fixedFinalDateAssembly: z.boolean(),
    warningMessage: z
      .string()
      .max(
        500,
        intl.formatMessage(
          { id: "error.maximum_character_count" },
          { value: 500 },
        ),
      )
      .optional()
      .refine(
        (warningMessage) => {
          if (watchShowWarningMessage) {
            return warningMessage.length > 0;
          }

          return true;
        },
        {
          message: intl.formatMessage({
            id: "error.field_required",
          }),
        },
      ),
    productPosition: z
      .string()
      .regex(/^(\d*)$/g, "error.need_to_be_number")
      .optional(),
  });

  const { control, watch, handleSubmit, reset, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: getDefaultFormValues(),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "scheduledValuesList",
  });
  const watchShowWarningMessage = watch("showWarningMessage");
  const watchFixedFinalDateAssembly = watch("fixedFinalDateAssembly");

  const pageTitleLabel = useMemo(
    () =>
      id
        ? "editing_product_event_association"
        : "new_product_event_association",
    [id],
  );

  const parseStringToDate = (dateString) => {
    if (!dateString) {
      return null;
    }
    return parse(dateString, "yyyy-MM-dd", new Date());
  };

  useEffect(() => {
    const fetchData = async () => {
      handleLoading(true);
      const config = {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      };

      try {
        const requests = [
          api.get(`/admin/events`, config),
          api.patch(`/admin/ecommerce/chleba/product`, {}, config),
          api.get(`/admin/ecommerce/chleba/unity`, config),
        ];

        if (id) {
          requests.push(
            api.get(`/admin/ecommerce/chleba/product-event/${id}`, config),
          );
        }

        const responses = await Promise.all(requests);
        const eventsResponse = responses[0].data;
        const productsResponse = responses[1].data;
        const unityResponse = responses[2].data;

        const events = eventsResponse.output.map((event) => ({
          label: event.eventEditionName,
          value: event.eventEditionCode,
        }));
        let eventsSort = [...events].sort((a, b) =>
          a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
        );
        setEvents(eventsSort);

        const products =
          productsResponse.ecommerceChlebaProductFilterOutput.map(
            (product) => ({
              label: language === "pt" ? product.title : product.englishTitle,
              value: product.id,
            }),
          );
        setProducts(products);

        const unities = unityResponse.ecommerceChlebaUnityOutput.map(
          (unity) => ({
            label: intl.formatMessage({ id: unity.title }),
            value: unity.id,
          }),
        );
        setUnities(unities);

        setPeriods([
          {
            label: intl.formatMessage({ id: "daytime" }),
            value: "DAYTIME",
          },
          {
            label: intl.formatMessage({ id: "nighttime" }),
            value: "NIGHTTIME",
          },
          {
            label: intl.formatMessage({ id: "both" }),
            value: "BOTH",
          },
        ]);

        if (id) {
          const productEventResponse = responses[3]?.data;
          const {
            deadline,
            maxDate,
            startDateAssembly,
            finalDateAssembly,
            scheduledValuesList,
            eventEditionCode,
            ...output
          } = productEventResponse.ecommerceChlebaProductEventOutput;
          const updatedScheduledValuesList = scheduledValuesList.map(
            (schedule) => ({
              ...schedule,
              scheduledDate: parseStringToDate(schedule.scheduledDate),
            }),
          );

          setLoadEditData({
            ...output,
            fixedFinalDateAssembly: output.fixedFinalDateAssembly ?? false,
            eventEditionCode: eventEditionCode,
            deadline: parseStringToDate(deadline),
            maxDate: parseStringToDate(maxDate),
            startDateAssembly: parseStringToDate(startDateAssembly),
            finalDateAssembly: parseStringToDate(finalDateAssembly),
            scheduledValuesList: updatedScheduledValuesList,
          });
          setSelectedEvent(eventEditionCode);
        } else {
          setInitialData(getDefaultFormValues());
        }
      } catch (err) {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      } finally {
        handleLoading(false);
      }
    };

    fetchData();
  }, [addToast, id, intl, token, handleLoading, language]);

  useEffect(() => {
    setValue("salesforceEquivalentProduct", null);
    if (selectedEvent) {
      handleLoading(true);
      api
        .get(`/product/get-salesforce-products-by-event/${selectedEvent}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((result) => {
          const selectSalesforceProducts = result.data.output.map(
            (product) => ({
              label: language === "pt" ? product.portugueseName : product.name,
              value: product.value,
            }),
          );
          let selectSalesforceProductsSort = [...selectSalesforceProducts].sort(
            (a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0),
          );
          setSalesforceProducts(selectSalesforceProductsSort);
        })
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          }),
        )
        .finally(() => handleLoading(false));
    }
  }, [addToast, intl, token, handleLoading, selectedEvent, language]);

  useEffect(() => {
    if (loadEditData && salesforceProducts && initialData === null) {
      const {
        eventEditionCode,
        productId,
        productType,
        unityId,
        period,
        salesforceProductCode,
      } = loadEditData;
      const selectedEventEdition = events.find(
        (event) => event.value === eventEditionCode,
      );
      const selectedProduct = products.find(
        (product) => product.value === productId,
      );
      const selectedProductType = productTypes.find(
        (productTypeItem) => productTypeItem.value === productType,
      );
      const selectedUnity = unities.find((unity) => unity.value === unityId);
      const selectedPeriod = periods.find(
        (periodEnum) => periodEnum.value === period,
      );
      const selectedSalesforceProduct = salesforceProducts.find(
        (product) => product.value === salesforceProductCode,
      );

      setInitialData({
        ...loadEditData,
        event: selectedEventEdition,
        product: selectedProduct,
        productType: selectedProductType,
        unity: selectedUnity,
        period: selectedPeriod || null,
        salesforceEquivalentProduct: selectedSalesforceProduct || null,
      });
    }
  }, [loadEditData, salesforceProducts, initialData]);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const createProductEventAssociation = async (
    createProductEventAssociationRequest,
    headerConfiguration,
  ) => {
    try {
      const result = await api.post(
        `/admin/ecommerce/chleba/product-event`,
        createProductEventAssociationRequest,
        headerConfiguration,
      );

      const { code } = result.data;
      const isPartialSuccess = code === 18017;

      addToast({
        type: isPartialSuccess ? "warning" : "success",
        title: intl.formatMessage({
          id: isPartialSuccess ? "partial_success" : "success",
        }),
        description: intl.formatMessage({
          id: `success.${code}`,
        }),
      });

      history.push("/admin/product-event-association");
    } catch (err) {
      const errorCode = err?.response?.data?.code;

      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: `api.error.${errorCode}`,
        }),
      });

      if ([-18000, -18003].includes(errorCode)) {
        history.push("/admin/product-event-association");
      }
    }
  };

  const updateProductEventAssociation = async (
    updateProductEventAssociationRequest,
    headerConfiguration,
  ) => {
    try {
      const result = await api.put(
        `/admin/ecommerce/chleba/product-event/${id}`,
        updateProductEventAssociationRequest,
        headerConfiguration,
      );

      const { code } = result.data;
      const isPartialSuccess = code === 18018;

      addToast({
        type: isPartialSuccess ? "warning" : "success",
        title: intl.formatMessage({
          id: isPartialSuccess ? "partial_success" : "success",
        }),
        description: intl.formatMessage({
          id: `success.${code}`,
        }),
      });

      history.push("/admin/product-event-association");
    } catch (err) {
      const errorCode = err?.response?.data?.code;

      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: `api.error.${errorCode}`,
        }),
      });
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    if (loading) {
      return;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
    };

    const refinedData = {
      eventEditionCode: data.event.value,
      productId: data.product.value,
      unityId: data.unity.value,
      realValue: data.realValue,
      dollarValue: data.dollarValue,
      euroValue: data.euroValue,
      deadline: data.deadline,
      maxDate: data.maxDate,
      startDateAssembly: data.startDateAssembly,
      finalDateAssembly: data.finalDateAssembly,
      fixedFinalDateAssembly: data.fixedFinalDateAssembly,
      period: data.period ? data.period.value : "",
      active: data.active,
      fractionatedQuantity: data.fractionatedQuantity,
      productType: data.productType.value,
      warningMessage: watchShowWarningMessage ? data.warningMessage : "",
      productPosition: data.productPosition,
      salesforceProductCode: data.salesforceEquivalentProduct
        ? data.salesforceEquivalentProduct.value
        : "",
      scheduledValuesList: data.scheduledValuesList,
    };

    try {
      if (id) {
        await updateProductEventAssociation(refinedData, config);
      } else {
        await createProductEventAssociation(refinedData, config);
      }
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "flow.unexpected.error",
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedMainContainer>
      <Container>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <TitleContainer>
            <Title>
              <FormattedMessage id={pageTitleLabel} />
            </Title>
            <ActionContainer>
              <SubmitContainer>
                <FormButton
                  color="#C9C9C9"
                  type="button"
                  onClick={() =>
                    history.push("/admin/product-event-association")
                  }
                  fontSize="12px"
                  marginTop="0px"
                >
                  <FormattedMessage id="goback" />
                </FormButton>
              </SubmitContainer>
              <SubmitContainer>
                <FormButton
                  type="submit"
                  loading={formState.isSubmitting}
                  disabled={formState.isSubmitting}
                  color="#002244"
                  fontSize="12px"
                  marginTop="0px"
                >
                  <FormattedMessage id="save" />
                </FormButton>
              </SubmitContainer>
            </ActionContainer>
          </TitleContainer>
          <Session>
            <FieldsContainer>
              <Horizontal>
                <ControlledSelect
                  name="event"
                  control={control}
                  errors={formState.errors}
                  label="event_edition"
                  placeholder={intl.formatMessage({ id: "event_edition" })}
                  required={true}
                  options={events}
                  onChange={(event) => setSelectedEvent(event?.value)}
                />
              </Horizontal>
              <Horizontal>
                <ControlledSelect
                  name="product"
                  control={control}
                  errors={formState.errors}
                  label="product"
                  placeholder={intl.formatMessage({ id: "product" })}
                  required={true}
                  options={products}
                />
                <ControlledSelect
                  name="productType"
                  control={control}
                  errors={formState.errors}
                  label="product_type"
                  placeholder={intl.formatMessage({ id: "product_type" })}
                  required={true}
                  options={productTypes}
                />
              </Horizontal>
              <Horizontal>
                <ControlledSelect
                  name="unity"
                  control={control}
                  errors={formState.errors}
                  label="unity"
                  placeholder={intl.formatMessage({ id: "unity" })}
                  required={true}
                  options={unities}
                />
                <ControlledSelect
                  name="period"
                  control={control}
                  errors={formState.errors}
                  label="period"
                  placeholder={intl.formatMessage({ id: "period" })}
                  options={periods}
                  clearable
                />
              </Horizontal>
              <Horizontal>
                <ControlledSelect
                  name="salesforceEquivalentProduct"
                  control={control}
                  errors={formState.errors}
                  label="salesforce_equivalent_product"
                  placeholder={intl.formatMessage({
                    id: "salesforce_equivalent_product",
                  })}
                  options={salesforceProducts}
                  clearable
                />
              </Horizontal>
              <Horizontal>
                <ControlledDatePicker
                  name="deadline"
                  control={control}
                  errors={formState.errors}
                  label="deadline"
                  tooltipText="deadline_tooltip"
                />
                <ControlledDatePicker
                  name="maxDate"
                  control={control}
                  errors={formState.errors}
                  label="maximum_payment_date"
                  tooltipText="max_date_tooltip"
                />
              </Horizontal>
              <Horizontal>
                <ControlledDatePicker
                  name="startDateAssembly"
                  control={control}
                  errors={formState.errors}
                  label="start_date_service"
                  tooltipText="assembly_date_tooltip"
                />
                <ControlledDatePicker
                  name="finalDateAssembly"
                  control={control}
                  errors={formState.errors}
                  label="final_date_service"
                  required={watchFixedFinalDateAssembly}
                  tooltipText="assembly_date_tooltip"
                />
              </Horizontal>
              <Horizontal>
                <div />
                <ControlledSwitch
                  name="fixedFinalDateAssembly"
                  control={control}
                  formState={formState}
                  label="fix_final_date_service"
                />
              </Horizontal>
              <Horizontal>
                <ControlledInput
                  name="productPosition"
                  control={control}
                  formState={formState}
                  label="position"
                  placeholder={intl.formatMessage({ id: "position" })}
                  inputMode="numeric"
                  min={1}
                />
                <ControlledInput
                  name="realValue"
                  control={control}
                  formState={formState}
                  label="R$"
                  placeholder="R$"
                  required={true}
                />
                <ControlledInput
                  name="dollarValue"
                  control={control}
                  formState={formState}
                  label="US$"
                  placeholder="US$"
                  required={true}
                />
                <ControlledInput
                  name="euroValue"
                  control={control}
                  formState={formState}
                  label="EU$"
                  placeholder="EU$"
                  required={true}
                />
              </Horizontal>
              {fields.map((item, index) => {
                return (
                  <Horizontal>
                    <ScheduleItem key={`${item.id}-${index}`}>
                      <IndexSchedule>
                        <span>
                          {intl.formatMessage({ id: "price_readjustment" })}{" "}
                          {index + 1}
                        </span>
                        <RemoveScheduleButton
                          type="button"
                          onClick={() => {
                            remove(index);
                          }}
                        >
                          <FaRegTrashAlt />
                          <FormattedMessage id="remove" />
                        </RemoveScheduleButton>
                      </IndexSchedule>
                      <Horizontal>
                        <ControlledDatePicker
                          key={`scheduledDate-${item.id}-${index}`}
                          name={`scheduledValuesList.${index}.scheduledDate`}
                          control={control}
                          errors={formState.errors}
                          arrayName="scheduledValuesList"
                          label="readjustment_date"
                          required={true}
                          minDate={new Date()}
                        />
                        <ControlledInput
                          key={`realScheduledValue-${item.id}-${index}`}
                          name={`scheduledValuesList.${index}.realScheduledValue`}
                          control={control}
                          formState={formState}
                          arrayName="scheduledValuesList"
                          label="R$"
                          placeholder="R$"
                          required={true}
                        />
                        <ControlledInput
                          key={`dollarScheduledValue-${item.id}-${index}`}
                          name={`scheduledValuesList.${index}.dollarScheduledValue`}
                          control={control}
                          formState={formState}
                          arrayName="scheduledValuesList"
                          label="US$"
                          placeholder="US$"
                          required={true}
                        />
                        <ControlledInput
                          key={`euroScheduledValue-${item.id}-${index}`}
                          name={`scheduledValuesList.${index}.euroScheduledValue`}
                          control={control}
                          formState={formState}
                          arrayName="scheduledValuesList"
                          label="EU$"
                          placeholder="EU$"
                          required={true}
                        />
                      </Horizontal>
                    </ScheduleItem>
                  </Horizontal>
                );
              })}
              <Horizontal>
                <AddScheduleButton
                  type="button"
                  onClick={() =>
                    append({
                      scheduledDate: "",
                      realScheduledValue: "",
                      dollarScheduledValue: "",
                      euroScheduledValue: "",
                    })
                  }
                >
                  <RiAddCircleLine size={19} />
                  <FormattedMessage id="add_price_readjustment" />
                </AddScheduleButton>
              </Horizontal>
              <Horizontal>
                <ControlledSwitch
                  name="active"
                  control={control}
                  formState={formState}
                  label="active"
                />
                <ControlledSwitch
                  name="showWarningMessage"
                  control={control}
                  formState={formState}
                  label="show_warning"
                />
                <ControlledSwitch
                  name="fractionatedQuantity"
                  control={control}
                  formState={formState}
                  label="fractionated"
                />
              </Horizontal>
              <Horizontal>
                <ControlledTextareaInput
                  name="warningMessage"
                  control={control}
                  formState={formState}
                  label="warning_message"
                  disabled={!watchShowWarningMessage}
                  required={watchShowWarningMessage}
                  min={1}
                  max={500}
                />
              </Horizontal>
            </FieldsContainer>
          </Session>
        </form>
      </Container>
    </AnimatedMainContainer>
  );
};

export default ProductEventAssociationItem;
